import {
  Button,
  FormControl,
  FormErrorMessage,
  FormLabel,
  HStack,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Stack,
  Text,
  useDisclosure,
} from '@elkaso-app/web-design';
import { useGetCreditFacilityApi } from 'apis/bnpl';
import { useUpdateCreditFacilityStatusApi } from 'apis/bnpl';
import {
  FacilityStatus,
  facilityStatusLabels,
  facilityStatusTransitions,
} from 'pages/bnpl/utils/bnpl-statuses';
import React from 'react';
import { Controller, useForm } from 'react-hook-form';
import { ChakraSelect } from '@elkaso-app/web-design';

interface UpdateFacilityStatusModalProps {
  trigger: JSX.Element;
  facilityId: number;
  currentStatus: string;
}

interface FormInputs {
  facilityStatus: { value: string; label: string } | null;
}

export const UpdateFacilityStatusModal = ({ trigger, facilityId, currentStatus }: UpdateFacilityStatusModalProps) => {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const { mutate: updateStatus, isLoading } = useUpdateCreditFacilityStatusApi();
  const { refetch } = useGetCreditFacilityApi(facilityId, { enabled: false });

  const allowedTransitions = facilityStatusTransitions[currentStatus as FacilityStatus] || [];

  const statusOptions = allowedTransitions.map((status) => ({
    value: status,
    label: facilityStatusLabels[status],
  }));

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<FormInputs>({
    mode: 'onChange',
    defaultValues: {
      facilityStatus: null,
    },
  });

  const onCloseModal = () => {
    onClose();
    reset({ facilityStatus: null });
  };

  const onSubmit = (data: FormInputs) => {
    if (!data.facilityStatus) return;

    updateStatus(
      { facilityId, body: { facilityStatus: data.facilityStatus.value } },
      {
        onSuccess: () => {
          refetch();
          onCloseModal();
        },
      }
    );
  };

  return (
    <>
      {React.cloneElement(trigger, { onClick: onOpen })}

      <Modal isOpen={isOpen} onClose={onCloseModal} closeOnOverlayClick={false} size='md'>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Update Facility Status</ModalHeader>
          <ModalBody>
            <Stack as='form' id='updateFacilityStatusForm' spacing='lg' onSubmit={handleSubmit(onSubmit)} noValidate>
              <Text fontSize='sm' color='gray.500'>
                Current status: {facilityStatusLabels[currentStatus as FacilityStatus] || currentStatus}
              </Text>

              {allowedTransitions.length === 0 ? (
                <Text fontSize='sm' color='gray.400'>
                  No status transitions available
                </Text>
              ) : (
                <Controller
                  control={control}
                  name='facilityStatus'
                  rules={{ required: 'Please select a status' }}
                  render={({ field: { onChange, onBlur, value } }) => (
                    <FormControl isInvalid={!!errors.facilityStatus} isRequired>
                      <FormLabel>New Status</FormLabel>
                      <ChakraSelect
                        placeholder='Select status'
                        options={statusOptions}
                        onChange={onChange}
                        onBlur={onBlur}
                        value={value}
                      />
                      <FormErrorMessage>{errors.facilityStatus?.message}</FormErrorMessage>
                    </FormControl>
                  )}
                />
              )}
            </Stack>
          </ModalBody>

          <ModalFooter>
            <HStack spacing='md'>
              <Button variant='outline' colorScheme='red' onClick={onCloseModal}>
                Cancel
              </Button>
              <Button
                variant='solid'
                colorScheme='red'
                type='submit'
                form='updateFacilityStatusForm'
                isLoading={isLoading}
                isDisabled={allowedTransitions.length === 0}>
                Update Status
              </Button>
            </HStack>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </>
  );
};
